// Third-party
import chroma from 'chroma-js';
import randomColor from 'randomcolor';

// Utils
import { getPatternDefinitions, ChartPattern } from './patterns';
import Dictionary from '@/types/dictionary';

export type SunburstColors = {
  fundType: string | ChartPattern;
  weight: string[] | ChartPattern[];
};

export enum COLORS {
  BLUE = 'blue',
  RED = 'red',
  GREEN = 'green',
  VIOLET = 'violet',
  PURPLE = 'purple',
  TEAL = 'teal',
  GREY = 'grey',
}

export enum PILL_COLORS {
  BLUE = 'blue',
  RED = 'red',
  GREEN = 'green',
  VIOLET = 'violet',
  PURPLE = 'purple',
  TEAL = 'teal',
  GREY = 'grey',
  ORANGE = 'orange',
  YELLOW = 'yellow',
}

export const ARROW_COLORS: Dictionary<string> = {
  down: '#AE1820',
  'full-down': '#AE1820',
  'full-up': '#AE1820',
  up: '#006A37',
};

export let ITEM_COLORS: string[] = [
  '#326ece',
  '#2ea87a',
  '#db3587',
  '#F26D2B',
  '#a127c8',
  '#04a2c7',
  '#c18b2d',
  '#0e795f',
  '#6149fa',
  '#0a7293',
  '#d42c2c',
  '#62a600',
  '#a25154',
  '#467600',
  '#6b6b6b',
];

export const ITEM_COLORS_THEME_LIGHT: string[] = [
  '#2B4ECC',
  '#559E1A',
  '#C91F96',
  '#7A4CC5',
  '#D67400',
  '#00735A',
  '#14553C',
  '#7B6549',
  '#8A0B5A',
  '#66428A',
  '#747474',
];

export const DEFAULT_COMPARISON_ITEM_COLOR = '#41889F';
export const DEFAULT_TARGET_COLOR = '#747474';

const BASE_COLORS: Dictionary<SunburstColors> = {
  FI: {
    fundType: '#979797',
    weight: ['#797979', '#43434c', '#3f4344'],
  },
  CASH: {
    fundType: '#dedede',
    weight: ['#d1d1d1', '#c1c1c2', '#cccccc'],
  },
};

export const contactPastilleLight: any = {
  bgColor: '#f2f2f2',
  borderColor: '#0000001A',
};

const getSimilar = (color: string): string[] => {
  const chromaColor = chroma(color);
  return [0.3, 0.6, 1].map(val => chromaColor.darken(val).hex());
};

const isLight = (color: string, treshold = 0.8) => chroma(color).luminance() > treshold;

const getCssGradient = (color: string = '#ffffff', ratio = 1, direction: 'left' | 'right' = 'left') =>
  `linear-gradient(to ${direction}, ${color}, ${chroma(color).darken(ratio)})`;

const getSunburstDictionary = (color: string): Dictionary<SunburstColors> => {
  const similar = getSimilar(color);
  return {
    ...BASE_COLORS,
    EQ: {
      fundType: color,
      weight: similar,
    },
    OTHER: getPatternDefinitions([color, ...similar]),
  };
};

/**
 * Override the item colors list.
 * @param colors The string array with the list of hex-formatted colors to override.
 */
export const overrideComparisonItemColors = (colors: string[]) => {
  ITEM_COLORS = colors;
};

/**
 * Generates a unique color based on a string value.
 * @param input The string value to be converted to a color value.
 * @param luminosity The luminosity setting (light or dark).
 * @returns {string} Hex-formatted color.
 */
export const getColorFromString = (input: string, luminosity: 'light' | 'dark' = 'dark') =>
  randomColor({
    seed: input.split('').reverse().toString(), // Reverse ID for greater variety of colors
    luminosity,
    format: 'hex',
  });

export default { getSimilar, getSunburstDictionary, isLight, getCssGradient, getColorFromString };
